import { Alert, VStack, Button, HStack, Spinner, Text } from "@chakra-ui/react";

import { usePendingInvitationsQuery } from "../lib/use-pending-invitations-query";
import { useRespondToInvitationMutation } from "../../respond/lib/use-respond-to-invitation-mutation";

import {
  invitationRoleSuffix,
  type PendingInvitation,
} from "@entities/invitation";
import { useAuthenticationContext } from "@app/auth/use-authentication-context";

interface Props {
  invitation: PendingInvitation;
}

const InvitationBannerItem: React.FC<Props> = ({ invitation }) => {
  const { revalidateSession } = useAuthenticationContext();
  const { mutate, isPending } = useRespondToInvitationMutation({
    onSuccess: (_, { action }) => {
      if (action === "accept") revalidateSession();
    },
  });

  return (
    <Alert.Root status="info" w="full">
      <Alert.Content>
        <Alert.Title>
          You've been invited to join{" "}
          <Text as="span" fontWeight="bold">
            {invitation.company?.name ?? "a company"}
          </Text>
          {invitationRoleSuffix(invitation.role)}
        </Alert.Title>
        <Alert.Description>
          <HStack mt={2} gap={2}>
            <Button
              size="xs"
              colorPalette="green"
              onClick={() =>
                mutate({ token: invitation.token, action: "accept" })
              }
              disabled={isPending}
            >
              {isPending ? <Spinner size="xs" /> : "Accept"}
            </Button>
            <Button
              size="xs"
              variant="outline"
              onClick={() =>
                mutate({ token: invitation.token, action: "decline" })
              }
              disabled={isPending}
            >
              Decline
            </Button>
          </HStack>
        </Alert.Description>
      </Alert.Content>
    </Alert.Root>
  );
};

export const PendingInvitationsBanner: React.FC = () => {
  const { data: invitations = [] } = usePendingInvitationsQuery();

  if (!invitations.length) return null;

  return (
    <VStack w="full" gap={2} px={{ base: 4, md: 0 }} pt={2}>
      {invitations.map((invitation) => (
        <InvitationBannerItem key={invitation.id} invitation={invitation} />
      ))}
    </VStack>
  );
};
